import { useStoreBoot } from '@/api/useStoreBoot';
import { ELoginMode } from '@/lib/enums';
import { BootData } from '@/lib/types';
import { getButtonStyle } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { LoginButton } from '@/components/portal-ui';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AppleLoginButton } from '../External/AppleLoginButton';
import { FacebookLoginButton } from '../External/FacebookLoginButton';
import { GoogleLoginButton } from '../External/GoogleLoginButton';
import { useTranslations } from 'next-intl';

export const LoginMethodSelector = () => {
  const t = useTranslations();
  const { initialBoot } = useAppState();
  const { getBoot } = useStoreBoot();
  const { setLoginType, shouldDisableLoginButtons } = useLoginState();
  const bootData = getBoot.data as BootData;

  const {
    otpOn,
    userTokenOn,
    fbOn,
    googleOn,
    googleAppId,
    fbAppId,
    appleAppId,
    appleOn,
    usernamePasswordOn
  } = bootData?.integration?.playersAuthentication ?? {};

  // Show loading spinner while boot data is being fetched
  if (getBoot.isLoading || getBoot.isFetching || !bootData) {
    return <LoadingSpinner text={t('portal.login.loadingLoginOptions')} />;
  }

  const otpConfig = initialBoot?.theme?.loginPopup?.otpButton;
  const playerIdConfig = initialBoot?.theme?.loginPopup?.playerIdButton;
  const fallbackColor = initialBoot?.theme?.loginPopup?.fontColor || '#FFF';

  return (
    <div className='mt-4 flex flex-col'>
      <GoogleOAuthProvider clientId={googleAppId}>
        {otpOn && (
          <LoginButton
            textColor={otpConfig?.textColor ?? { colorOne: fallbackColor }}
            backgroundColor={otpConfig?.backgroundColor ?? { colorOne: 'transparent' }}
            text={otpConfig?.text ?? t('portal.login.signInWithOtp')}
            callback={() => setLoginType(ELoginMode.OTP)}
            dataTestId='otp-login-button'
            isDisabled={shouldDisableLoginButtons}
            iconSrc={otpConfig?.icon}
            isModalMode
          />
        )}
        {fbOn && fbAppId && <FacebookLoginButton />}
        {googleOn && googleAppId && <GoogleLoginButton />}
        {appleOn && appleAppId && <AppleLoginButton />}
        {userTokenOn && (
          <LoginButton
            textColor={
              playerIdConfig?.textColor ?? getButtonStyle(initialBoot?.theme).color
            }
            backgroundColor={
              playerIdConfig?.backgroundColor ??
              getButtonStyle(initialBoot?.theme)?.background
            }
            variant='transparent'
            text={playerIdConfig?.text || t('portal.login.signInWithPlayerId')}
            callback={() => setLoginType(ELoginMode.TOKEN_ID)}
            dataTestId='playerId-login-button'
            isDisabled={shouldDisableLoginButtons}
            iconSrc={playerIdConfig?.icon}
            isModalMode
          />
        )}
        {usernamePasswordOn && (
          <LoginButton
            textColor={getButtonStyle(initialBoot?.theme).color}
            backgroundColor={getButtonStyle(initialBoot?.theme)?.background}
            variant='transparent'
            text={t('portal.login.signInWithUsername')}
            callback={() => setLoginType(ELoginMode.USERNAME)}
            dataTestId='username-login-button'
            isDisabled={shouldDisableLoginButtons}
            isModalMode
          />
        )}
      </GoogleOAuthProvider>
    </div>
  );
};
